import React from 'react';
import './App.css';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { LanguageProvider } from './contexts/LanguageContext';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { SocialProvider } from './contexts/SocialContext';
import { FacebookPixel } from './components/FacebookPixel';
import { SocialMeta } from './components/SocialMeta';
import { SEO } from './components/SEO';
import { FacebookFeed } from './components/FacebookFeed';
import { Header } from './components/Header';
import { Hero } from './components/Hero';
import { Aktienregister } from './components/Aktienregister';
import { WarumLoewenherz } from './components/WarumLoewenherz';
import { Aktiengattungen } from './components/Aktiengattungen';
import { Services } from './components/Services';
import { Projects } from './components/Projects';
import { Investors } from './components/Investors';
import { Downloads } from './components/Downloads';
import { Contact } from './components/Contact';
import { Footer } from './components/Footer';
import { AdminLogin } from './pages/AdminLogin';
import { AdminDashboard } from './pages/AdminDashboard';
import { AdminProjects } from './pages/AdminProjects';
import { AdminUsers } from './pages/AdminUsers';
import { AdminMessages } from './pages/AdminMessages';
import { AdminDownloads } from './pages/AdminDownloads';
import { AdminAnalytics } from './pages/AdminAnalytics';
import { AdminTeam } from './pages/AdminTeam';
import { AdminMediators } from './pages/AdminMediators';
import { AdminShareholders } from './pages/AdminShareholders';
import { AdminPreemptiveDrafts } from './pages/AdminPreemptiveDrafts';
import { AdminValuation } from './pages/AdminValuation';
import { AdminProperties } from './pages/AdminProperties';
import { AdminCombinedValuation } from './pages/AdminCombinedValuation';
import { AdminBlog } from './pages/AdminBlog';
import { AdminBlogEditor } from './pages/AdminBlogEditor';
import { BlogList } from './pages/BlogList';
import { BlogDetail } from './pages/BlogDetail';
import { PreemptiveOffer } from './pages/PreemptiveOffer';
import { MediatorLogin } from './pages/MediatorLogin';
import { MediatorDashboard } from './pages/MediatorDashboard';
import { MediatorRegister } from './pages/MediatorRegister';
import { InvestorDashboard } from './pages/InvestorDashboard';
import { AdminSubscriptions } from './pages/AdminSubscriptions';
import { AdminSettings } from './pages/AdminSettings';
import { Impressum } from './pages/Impressum';
import { Datenschutz } from './pages/Datenschutz';
import { AGB } from './pages/AGB';
import { Risikohinweise } from './pages/Risikohinweise';
import { ReferralTracker } from './components/ReferralTracker';
import { Toaster } from './components/ui/sonner';

// Protected Route Component
const ProtectedRoute = ({ children, allowed = ['admin'], redirectTo }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-gray-400">Lädt...</div>
      </div>
    );
  }

  if (!user || !allowed.includes(user.role)) {
    // Sensible default per role
    let target = redirectTo;
    if (!target) {
      if (allowed.includes('investor')) target = '/';
      else if (allowed.includes('mediator')) target = '/vermittler/login';
      else target = '/admin/login';
    }
    return <Navigate to={target} replace />;
  }

  return children;
};

// Public Landing Page
const LandingPage = () => {
  const location = useLocation();

  // Capture ?ref=CODE -> save to localStorage so Investor registration can use it
  React.useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const ref = params.get('ref');
      if (ref) {
        localStorage.setItem('loewenherz_ref', ref.toUpperCase());
      }
    } catch (e) {
      // ignore
    }
  }, []);

  // Scroll to section when hash is present (e.g. arriving from /impressum -> /#projects).
  // We retry a few times because sections may need time to mount and lay out.
  React.useEffect(() => {
    if (!location.hash) return;
    const id = decodeURIComponent(location.hash.slice(1));
    let attempts = 0;
    const tryScroll = () => {
      attempts += 1;
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        return;
      }
      if (attempts < 20) window.setTimeout(tryScroll, 100);
    };
    const initial = window.setTimeout(tryScroll, 100);
    return () => window.clearTimeout(initial);
  }, [location.hash, location.key]);

  return (
    <div className="App bg-black min-h-screen">
      <Header />
      <Hero />
      <Aktienregister />
      <WarumLoewenherz />
      <Aktiengattungen />
      <Services />
      <Projects />
      <Investors />
      <FacebookFeed />
      <Downloads />
      <Contact />
      <Footer />
    </div>
  );
};

// Auto-scrolls the window to the top whenever the route changes to a path
// without a hash. Allows hash-based scroll-to-section to keep working.
const ScrollToTop = () => {
  const location = useLocation();
  React.useEffect(() => {
    if (location.hash) return;
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [location.pathname, location.hash]);
  return null;
};

function App() {
  return (
    <AuthProvider>
      <LanguageProvider>
        <SocialProvider>
          <BrowserRouter>
            <ScrollToTop />
            <ReferralTracker />
            <SocialMeta />
            <SEO />
            <FacebookPixel />
            <Routes>
            {/* Public Routes */}
            <Route path="/" element={<LandingPage />} />
            <Route path="/blog" element={<BlogList />} />
            <Route path="/blog/:slug" element={<BlogDetail />} />
            <Route path="/impressum" element={<Impressum />} />
            <Route path="/datenschutz" element={<Datenschutz />} />
            <Route path="/agb" element={<AGB />} />
            <Route path="/risikohinweise" element={<Risikohinweise />} />
            
            {/* Admin Routes */}
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/bezugsrecht/:token" element={<PreemptiveOffer />} />
            <Route
              path="/admin/dashboard"
              element={
                <ProtectedRoute allowed={['admin', 'editor']}>
                  <AdminDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/projects"
              element={
                <ProtectedRoute allowed={['admin', 'editor']}>
                  <AdminProjects />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/users"
              element={
                <ProtectedRoute>
                  <AdminUsers />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/messages"
              element={
                <ProtectedRoute allowed={['admin', 'editor']}>
                  <AdminMessages />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/downloads"
              element={
                <ProtectedRoute allowed={['admin', 'editor']}>
                  <AdminDownloads />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/analytics"
              element={
                <ProtectedRoute>
                  <AdminAnalytics />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/team"
              element={
                <ProtectedRoute>
                  <AdminTeam />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/mediators"
              element={
                <ProtectedRoute>
                  <AdminMediators />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/shareholders"
              element={
                <ProtectedRoute allowed={['admin', 'editor']}>
                  <AdminShareholders />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/preemptive/drafts"
              element={
                <ProtectedRoute allowed={['admin']}>
                  <AdminPreemptiveDrafts />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/valuation"
              element={
                <ProtectedRoute allowed={['admin']}>
                  <AdminValuation />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/properties"
              element={
                <ProtectedRoute allowed={['admin']}>
                  <AdminProperties />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/valuation/combined"
              element={
                <ProtectedRoute allowed={['admin']}>
                  <AdminCombinedValuation />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/blog"
              element={
                <ProtectedRoute allowed={['admin', 'editor']}>
                  <AdminBlog />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/blog/new"
              element={
                <ProtectedRoute allowed={['admin', 'editor']}>
                  <AdminBlogEditor />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/blog/:id/edit"
              element={
                <ProtectedRoute allowed={['admin', 'editor']}>
                  <AdminBlogEditor />
                </ProtectedRoute>
              }
            />
            <Route path="/vermittler/login" element={<MediatorLogin />} />
            <Route path="/vermittler/register" element={<MediatorRegister />} />
            <Route
              path="/mediator/dashboard"
              element={
                <ProtectedRoute allowed={['mediator']}>
                  <MediatorDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/investor/dashboard"
              element={
                <ProtectedRoute allowed={['investor']}>
                  <InvestorDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/subscriptions"
              element={
                <ProtectedRoute allowed={['admin', 'editor']}>
                  <AdminSubscriptions />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/settings"
              element={
                <ProtectedRoute allowed={['admin']}>
                  <AdminSettings />
                </ProtectedRoute>
              }
            />

            {/* Catch all - redirect to home */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          <Toaster position="top-right" theme="dark" offset={80} />
          </BrowserRouter>
        </SocialProvider>
      </LanguageProvider>
    </AuthProvider>
  );
}

export default App;